import { BinParsingConfig, parseBinValue } from "./bin-parsing";
import { WarehouseGrid, Zone } from "./schema/warehouse-grid";
import {
  getCompiledZoneBinPatterns,
  matchZoneBinPattern,
  ZoneBinPatternMatch,
} from "./zone-bin-patterns";
import { pointInRectangle } from "./zone-rectangles";

export type ZoneResolutionGrid = Pick<
  WarehouseGrid,
  "zones" | "zoneSequence"
> & { binCoordinates?: Record<string, { x: number; y: number }> | null };

export type BinZoneResolution =
  | { zoneId: string; basis: "rectangle" }
  | { zoneId: string; basis: "pattern"; match: ZoneBinPatternMatch }
  | { zoneId: string | null; basis: "default" };

export function resolveBinZone(
  binName: string,
  grid: ZoneResolutionGrid,
  binParsingConfig?: BinParsingConfig,
): string | null {
  return explainBinZone(binName, grid, binParsingConfig).zoneId;
}

export function explainBinZone(
  binName: string,
  grid: ZoneResolutionGrid,
  binParsingConfig?: BinParsingConfig,
): BinZoneResolution {
  const zones = grid.zones;
  if (!zones) {
    return { zoneId: null, basis: "default" };
  }
  const zoneEntries = Object.entries(zones);
  if (zoneEntries.length === 0) {
    return { zoneId: null, basis: "default" };
  }

  const defaultZoneId = resolveDefaultZoneIdFromEntries(grid, zoneEntries);
  const { coordinateCandidates, patternCandidates } = binCandidates(
    binName,
    binParsingConfig,
  );

  let sawCoordinate = false;
  for (const candidate of coordinateCandidates) {
    const coord =
      grid.binCoordinates?.[candidate] ??
      grid.binCoordinates?.[candidate.toUpperCase()];
    if (!coord) {
      continue;
    }
    sawCoordinate = true;
    for (const [zoneId, zone] of zoneEntries) {
      if (zone.isDefault) {
        continue;
      }
      for (const rect of zone.rectangles) {
        if (pointInRectangle(coord, rect)) {
          return { zoneId, basis: "rectangle" };
        }
      }
    }
  }
  if (sawCoordinate) {
    return { zoneId: defaultZoneId, basis: "default" };
  }

  const match = matchZoneBinPattern(
    patternCandidates,
    getCompiledZoneBinPatterns(grid),
  );
  if (match) {
    return { zoneId: match.zoneId, basis: "pattern", match };
  }
  return { zoneId: defaultZoneId, basis: "default" };
}

function binCandidates(
  binName: string,
  binParsingConfig: BinParsingConfig | undefined,
): { coordinateCandidates: string[]; patternCandidates: string[] } {
  if (!binParsingConfig) {
    return { coordinateCandidates: [binName], patternCandidates: [binName] };
  }
  const { primary, secondary } = parseBinValue(binName, binParsingConfig);
  const parsed = primary ? [primary, ...secondary] : [];
  return {
    coordinateCandidates: parsed,
    patternCandidates: [binName, ...parsed],
  };
}

export function resolveDefaultZoneId(
  grid: Pick<WarehouseGrid, "zones" | "zoneSequence">,
): string | null {
  const zones = grid.zones;
  if (!zones) {
    return null;
  }
  const zoneEntries = Object.entries(zones);
  if (zoneEntries.length === 0) {
    return null;
  }
  return resolveDefaultZoneIdFromEntries(grid, zoneEntries);
}

function resolveDefaultZoneIdFromEntries(
  grid: Pick<WarehouseGrid, "zones" | "zoneSequence">,
  zoneEntries: [string, Zone][],
): string | null {
  const explicit = zoneEntries.find(([, z]) => z.isDefault)?.[0];
  if (explicit !== undefined) {
    return explicit;
  }
  if (grid.zoneSequence) {
    for (const zoneId of grid.zoneSequence) {
      if (Object.prototype.hasOwnProperty.call(grid.zones ?? {}, zoneId)) {
        return zoneId;
      }
    }
  }
  return zoneEntries[0]?.[0] ?? null;
}

export function resolveFirstZone(
  binNames: string[],
  grid: ZoneResolutionGrid,
  zoneSequence: string[],
  binParsingConfig?: BinParsingConfig,
): string | null {
  if (binNames.length === 0 || zoneSequence.length === 0) {
    return null;
  }

  const zoneIdsForBins = new Set<string>();
  for (const binName of binNames) {
    const zoneId = resolveBinZone(binName, grid, binParsingConfig);
    if (zoneId !== null) {
      zoneIdsForBins.add(zoneId);
    }
  }
  if (zoneIdsForBins.size === 0) {
    return null;
  }

  for (const zoneId of zoneSequence) {
    if (zoneIdsForBins.has(zoneId)) {
      return zoneId;
    }
  }
  return null;
}
